import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import Empty from '../../../shared/components/Empty';
import PageHeader from '../../../shared/components/PageHeader';
import {
  DEFAULT_ADMIN_FILTERS,
  filterAdminItems,
  hasActiveAdminFilters,
  type AdminFilters,
  type AdminTab,
} from '../helpers/adminFilters';
import AdminFilterModal from './Parts/AdminFilterModal';
import AdminPagination from './Parts/AdminPagination';
import AdminTabs from './Parts/AdminTabs';
import AdminToolbar from './Parts/AdminToolbar';
import HistoryList from './Parts/HistoryList';
import InquiriesList from './Parts/InquiriesList';
import ReviewsList from './Parts/ReviewsList';
import TagsList from './Parts/TagsList';
import UserModal from './Parts/UserModal';
import UsersList from './Parts/UsersList';

type AdminPageProps = {
  places: any[];
  users: any[];
  inquiries: any[];
  tags?: any[];
  onCreateTag?: (name: string) => Promise<boolean>;
  onDeleteTag?: (tag: any) => Promise<boolean | void>;
  onToggleBan: (user: any) => Promise<boolean>;
  onOpen: (place: any) => void;
  back: () => void;
  t: any;
  renderInquiry: (inquiry: any, onClose: () => void) => ReactNode;
};

const ITEMS_PER_PAGE = 10;

export default function AdminPage({
  places,
  users,
  inquiries,
  tags = [],
  onCreateTag = async () => false,
  onDeleteTag = async () => {},
  onToggleBan,
  onOpen,
  back,
  t,
  renderInquiry,
}: AdminPageProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searches, setSearches] = useState<Record<AdminTab, string>>({
    users: '',
    history: '',
    reviews: '',
    tags: '',
    inquiries: '',
  });
  const [filters, setFilters] = useState<Record<AdminTab, AdminFilters>>(() => ({
    users: { ...DEFAULT_ADMIN_FILTERS.users },
    history: { ...DEFAULT_ADMIN_FILTERS.history },
    reviews: { ...DEFAULT_ADMIN_FILTERS.reviews },
    tags: { ...DEFAULT_ADMIN_FILTERS.tags },
    inquiries: { ...DEFAULT_ADMIN_FILTERS.inquiries },
  }));
  const [pages, setPages] = useState<Record<AdminTab, number>>({
    users: 1,
    history: 1,
    reviews: 1,
    tags: 1,
    inquiries: 1,
  });
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [selectedInquiryId, setSelectedInquiryId] = useState<string>();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId),
    [selectedUserId, users],
  );
  const selectedInquiry = useMemo(
    () => inquiries.find((inquiry) => inquiry.id === selectedInquiryId),
    [inquiries, selectedInquiryId],
  );
  const reviews = useMemo(
    () => places.flatMap((place) => (place.reviews || []).map((review: any) => ({
      ...review,
      id: `${place.id}-${review.authorId || review.createdAt}`,
      place,
      placeName: place.name,
    }))),
    [places],
  );
  const itemsByTab = { users, history: places, reviews, tags, inquiries };
  const visibleItems = filterAdminItems(
    activeTab,
    itemsByTab[activeTab],
    searches[activeTab],
    filters[activeTab],
    t.locale,
  );
  const totalPages = Math.max(1, Math.ceil(visibleItems.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(pages[activeTab], totalPages);
  const paginatedItems = visibleItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const filtersActive = hasActiveAdminFilters(activeTab, filters[activeTab]);

  const changeTab = (tab: AdminTab) => {
    setActiveTab(tab);
    setFilterOpen(false);
  };

  return (
    <PageHeader
      back={back}
      eyebrow={t.administration}
      title={t.admin}
      subtitle={t.adminSubtitle}
    >
      <AdminTabs
        activeTab={activeTab}
        counts={{ users: users.length, places: places.length, reviews: reviews.length, tags: tags.length, inquiries: inquiries.length }}
        labels={{ users: t.users, history: t.entries, reviews: t.reviews, tags: t.tags, inquiries: t.inquiries }}
        onChange={changeTab}
      />
      <AdminToolbar
        active={filtersActive}
        search={searches[activeTab]}
        t={t}
        showFilter={activeTab !== 'tags'}
        onFilter={() => setFilterOpen(true)}
        onSearch={(search) => {
          setSearches((current) => ({ ...current, [activeTab]: search }));
          setPages((current) => ({ ...current, [activeTab]: 1 }));
        }}
      />
      {!visibleItems.length && activeTab !== 'tags' && <Empty icon={MagnifyingGlass} title={t.noMatches} />}
      {!!paginatedItems.length && activeTab === 'users' && (
        <UsersList users={paginatedItems} now={now} t={t} onSelect={setSelectedUserId} />
      )}
      {!!paginatedItems.length && activeTab === 'history' && (
        <HistoryList places={paginatedItems} t={t} onOpen={onOpen} />
      )}
      {!!paginatedItems.length && activeTab === 'reviews' && (
        <ReviewsList reviews={paginatedItems} t={t} onOpen={onOpen} />
      )}
      {activeTab === 'tags' && (
        <TagsList tags={paginatedItems} t={t} onCreate={onCreateTag} onDelete={onDeleteTag} />
      )}
      {!!paginatedItems.length && activeTab === 'inquiries' && (
        <InquiriesList inquiries={paginatedItems} t={t} onSelect={setSelectedInquiryId} />
      )}
      {visibleItems.length > ITEMS_PER_PAGE && (
        <AdminPagination
          page={currentPage}
          totalPages={totalPages}
          t={t}
          onChange={(page) => setPages((current) => ({ ...current, [activeTab]: page }))}
        />
      )}
      {filterOpen && (
        <AdminFilterModal
          filters={filters[activeTab]}
          tab={activeTab}
          t={t}
          onClose={() => setFilterOpen(false)}
          onApply={(nextFilters) => {
            setFilters((current) => ({ ...current, [activeTab]: nextFilters }));
            setPages((current) => ({ ...current, [activeTab]: 1 }));
            setFilterOpen(false);
          }}
        />
      )}
      {selectedUser && (
        <UserModal
          user={selectedUser}
          now={now}
          t={t}
          onClose={() => setSelectedUserId(undefined)}
          onToggleBan={onToggleBan}
        />
      )}
      {selectedInquiry && renderInquiry(
        selectedInquiry,
        () => setSelectedInquiryId(undefined),
      )}
    </PageHeader>
  );
}
